/**
 * ScheduledMessageDetailsTab - Configuration tab for scheduled_message follow-ups.
 * Redesigned with collapsible sections, visual hierarchy, and schedule summary.
 */
import React, { useState } from 'react';
import {
  Clock, Send, Play, Check, Loader2, Settings, MessageSquare,
  AlertCircle, ChevronDown, ChevronUp, Calendar, Repeat, UserPlus,
} from 'lucide-react';
import InlineEditField from './InlineEditField';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplatePicker from '../messages/TemplatePicker';
import { templateCapabilities } from '../../utils/templateParams';
import { isBrPhoneInput, maskFollowupPhoneInput } from './followupPhone';

const StatusBadge = ({ status, colors }) => {
  const config = {
    draft: { label: 'Rascunho', bg: `${colors.textSecondary}20`, color: colors.textSecondary },
    active: { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' },
    paused: { label: 'Pausado', bg: '#f59e0b20', color: '#f59e0b' },
    completed: { label: 'Concluido', bg: '#6366f120', color: '#6366f1' },
  };
  const c = config[status] || config.draft;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};

const WEEKDAYS = [
  { value: 0, label: 'Seg' },
  { value: 1, label: 'Ter' },
  { value: 2, label: 'Qua' },
  { value: 3, label: 'Qui' },
  { value: 4, label: 'Sex' },
  { value: 5, label: 'Sáb' },
  { value: 6, label: 'Dom' },
];

const RECURRENCE_OPTIONS = [
  { value: 'once', label: 'Uma vez' },
  { value: 'daily', label: 'Diariamente' },
  { value: 'weekly', label: 'Semanalmente' },
  { value: 'monthly', label: 'Mensalmente' },
];

/** Reusable collapsible section */
const CollapsibleSection = ({ title, icon: Icon, defaultOpen = true, children, colors, accentColor }) => {
  const [open, setOpen] = useState(defaultOpen);
  const accent = accentColor || colors.accentPrimary;
  return (
    <div style={{
      backgroundColor: colors.bgPrimary, borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`,
      borderLeft: `3px solid ${accent}`,
      marginBottom: '16px', overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 16px',
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          fontSize: '14px', fontWeight: 600, color: colors.textPrimary,
        }}>
          {Icon && <Icon size={16} color={accent} />}
          {title}
        </span>
        {open ? (
          <ChevronUp size={16} color={colors.textSecondary} />
        ) : (
          <ChevronDown size={16} color={colors.textSecondary} />
        )}
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/** Format YYYY-MM-DD to DD/MM/YYYY */
const formatDateBR = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return dateStr || '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};


/** Build human-readable schedule summary */
const buildScheduleSummary = (schedule) => {
  if (!schedule) return '';
  const time = schedule.send_time || '09:00';
  const rec = schedule.recurrence || 'once';
  const start = formatDateBR(schedule.start_date);
  const end = formatDateBR(schedule.end_date);

  let text = '';
  if (rec === 'once') {
    text = `Envia uma vez as ${time}`;
    if (start) text += ` em ${start}`;
  } else if (rec === 'daily') {
    text = `Envia diariamente às ${time}`;
  } else if (rec === 'weekly') {
    const dayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
    const days = (schedule.recurrence_days || []).map(d => dayNames[d]).join(', ');
    text = `Envia semanalmente (${days || 'nenhum dia'}) às ${time}`;
  } else if (rec === 'monthly') {
    text = `Envia mensalmente no dia ${schedule.recurrence_day_of_month || 1} às ${time}`;
  }

  if (rec !== 'once') {
    if (start) text += `, de ${start}`;
    if (end) text += ` ate ${end}`;
    else if (start) text += ' (sem data fim)';
  }

  return text;
};

const ScheduledMessageDetailsTab = ({
  followup,
  colors,
  styles,
  testNumber,
  setTestNumber,
  sendingTest,
  onSendNow,
  onActivate,
  onDeactivate,
  onUpdateFollowup,
  onOpenMessageEditor,
  isBusinessChannel,
  approvedTemplates = [],
  onOpenContactPicker,
  canCreateRecurring = true,
}) => {
  const f = followup;
  const schedule = f.schedule || {};
  // Seletor único de template (2026-09-14). Sem a flag, como antes.
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);
  const messages = f.messages || [];

  const updateSchedule = (field, value) => {
    const newSchedule = { ...schedule, [field]: value };
    // Auto-fill required dependent fields when switching recurrence type
    if (field === 'recurrence') {
      if (value === 'weekly' && (!newSchedule.recurrence_days || newSchedule.recurrence_days.length === 0)) {
        newSchedule.recurrence_days = [0]; // Monday as default
      }
      if (value === 'monthly' && !newSchedule.recurrence_day_of_month) {
        newSchedule.recurrence_day_of_month = 1; // Day 1 as default
      }
    }
    onUpdateFollowup({ schedule: newSchedule });
  };

  const scheduleSummary = buildScheduleSummary(schedule);

  // Accent color for scheduled_message (teal to differentiate from challenge_sequence)
  const ACCENT = '#0ea5e9';

  return (
    <>
      {/* Summary Card */}
      <div style={{
        ...styles.card, marginBottom: '20px', cursor: 'default',
        borderLeft: `4px solid ${ACCENT}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ flex: 1, marginRight: '12px' }}>
            <InlineEditField
              value={f.name}
              onSave={(val) => onUpdateFollowup({ name: val })}
              placeholder="Nome da mensagem agendada"
              colors={colors}
              textStyle={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}
              inputStyle={{ fontSize: '18px', fontWeight: 600 }}
            />
            <div style={{ marginTop: '4px' }}>
              <InlineEditField
                value={f.description}
                onSave={(val) => onUpdateFollowup({ description: val })}
                placeholder="Adicionar descrição..."
                multiline
                colors={colors}
                textStyle={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}
                inputStyle={{ fontSize: '13px' }}
              />
            </div>
          </div>
          <StatusBadge status={f.status} colors={colors} />
        </div>

        {/* Schedule Summary Line */}
        {scheduleSummary && (
          <div style={{
            padding: '8px 12px', borderRadius: '8px',
            backgroundColor: `${ACCENT}08`, border: `1px solid ${ACCENT}15`,
            fontSize: '13px', color: ACCENT, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: '6px',
            marginTop: '8px',
          }}>
            <Calendar size={14} />
            {scheduleSummary}
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: colors.textSecondary, marginTop: '10px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MessageSquare size={12} /> {messages.length} mensagen{messages.length !== 1 ? 's' : ''}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Repeat size={12} /> {RECURRENCE_OPTIONS.find(o => o.value === (schedule.recurrence || 'once'))?.label || 'Uma vez'}
          </span>
        </div>
      </div>

      {/* 1. Schedule Configuration */}
      <CollapsibleSection title="Agendamento" icon={Calendar} defaultOpen={true} colors={colors} accentColor={ACCENT}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Send Time */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
              Horário de envio
            </label>
            <input
              type="time"
              value={schedule.send_time || '09:00'}
              onChange={(e) => updateSchedule('send_time', e.target.value)}
              style={{
                padding: '10px 12px', borderRadius: '8px',
                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                color: colors.textPrimary, fontSize: '14px', outline: 'none',
              }}
            />
          </div>

          {/* Recurrence */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
              Recorrencia
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {RECURRENCE_OPTIONS.map(opt => {
                const isRecurring = opt.value !== 'once';
                const isLocked = isRecurring && !canCreateRecurring;
                return (
                  <button
                    key={opt.value}
                    onClick={() => updateSchedule('recurrence', opt.value)}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
                      fontWeight: 500, cursor: isLocked ? 'not-allowed' : 'pointer', border: 'none',
                      backgroundColor: (schedule.recurrence || 'once') === opt.value ? ACCENT : colors.bgSecondary,
                      color: (schedule.recurrence || 'once') === opt.value ? '#fff' : colors.textSecondary,
                      transition: 'all 0.15s',
                      opacity: isLocked ? 0.6 : 1,
                      position: 'relative',
                    }}
                  >
                    {opt.label}
                    {isLocked && (
                      <span style={{
                        position: 'absolute', top: '-8px', right: '-4px',
                        backgroundColor: ACCENT, color: 'white',
                        fontSize: '9px', fontWeight: 700, padding: '1px 6px',
                        borderRadius: '8px',
                      }}>PRO</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Weekly: Day selection */}
          {schedule.recurrence === 'weekly' && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                Dias da semana
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {WEEKDAYS.map(day => {
                  const isSelected = (schedule.recurrence_days || []).includes(day.value);
                  return (
                    <button
                      key={day.value}
                      onClick={() => {
                        const current = schedule.recurrence_days || [];
                        const updated = isSelected
                          ? current.filter(d => d !== day.value)
                          : [...current, day.value].sort();
                        updateSchedule('recurrence_days', updated);
                      }}
                      style={{
                        width: '42px', height: '42px', borderRadius: '50%', fontSize: '12px',
                        fontWeight: 600, cursor: 'pointer', border: 'none',
                        backgroundColor: isSelected ? ACCENT : colors.bgSecondary,
                        color: isSelected ? '#fff' : colors.textSecondary,
                        transition: 'all 0.15s',
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Monthly: Day of month */}
          {schedule.recurrence === 'monthly' && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                Dia do mês
              </label>
              <select
                value={schedule.recurrence_day_of_month || 1}
                onChange={(e) => updateSchedule('recurrence_day_of_month', parseInt(e.target.value))}
                style={{
                  padding: '10px 12px', borderRadius: '8px',
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                  color: colors.textPrimary, fontSize: '14px', outline: 'none',
                }}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>Dia {d}</option>
                ))}
              </select>
            </div>
          )}

          {/* Start/End dates */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                Data de início
              </label>
              <input
                type="date"
                value={schedule.start_date || ''}
                onChange={(e) => updateSchedule('start_date', e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                  color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            {schedule.recurrence && schedule.recurrence !== 'once' && (
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Data fim (opcional)
                </label>
                <input
                  type="date"
                  value={schedule.end_date || ''}
                  onChange={(e) => updateSchedule('end_date', e.target.value || null)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* 2. Messages */}
      <CollapsibleSection title="Mensagens" icon={MessageSquare} defaultOpen={true} colors={colors} accentColor={ACCENT}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <button
            onClick={onOpenMessageEditor}
            style={{
              ...styles.btn, padding: '6px 14px', fontSize: '12px',
              backgroundColor: ACCENT, color: 'white', border: 'none',
              borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <Settings size={14} /> Configurar
          </button>
        </div>

        {messages.length === 0 ? (
          <div
            onClick={onOpenMessageEditor}
            style={{
              textAlign: 'center', padding: '24px', color: colors.textSecondary,
              fontSize: '13px', cursor: 'pointer', borderRadius: '10px',
              border: `2px dashed ${ACCENT}30`,
              backgroundColor: `${ACCENT}05`,
              transition: 'all 0.15s',
            }}
          >
            Clique para configurar as mensagens
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {messages.map((msg, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 12px', borderRadius: '8px',
                backgroundColor: colors.bgSecondary,
                borderLeft: `3px solid ${ACCENT}30`,
              }}>
                <span style={{
                  width: '22px', height: '22px', borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 600,
                  backgroundColor: `${ACCENT}15`, color: ACCENT, flexShrink: 0,
                }}>
                  {idx + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '12px', color: colors.textPrimary, display: 'block' }}>
                    {msg.message_type === 'template' ? `Template: ${msg.template_name || '(sem nome)'}` :
                     msg.message_type === 'interactive' ? `Interativa (${msg.interactive_type || 'button'})` :
                     msg.text ? (msg.text.length > 60 ? msg.text.substring(0, 60) + '...' : msg.text) : '(arquivo)'}
                  </span>
                  {msg.files && msg.files.length > 0 && (
                    <span style={{ fontSize: '11px', color: colors.textTertiary }}>
                      {msg.files.length} arquivo{msg.files.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '10px', color: colors.textTertiary, whiteSpace: 'nowrap' }}>
                  {msg.message_type || 'custom'}
                </span>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* 3. Activation / Status */}
      <CollapsibleSection
        title={f.status === 'active' ? 'Status' : (f.status === 'paused' ? 'Retomar' : 'Ativar')}
        icon={f.status === 'active' ? Check : Play}
        defaultOpen={f.status !== 'active'}
        colors={colors}
        accentColor={f.status === 'active' ? '#22c55e' : ACCENT}
      >
        {f.status === 'active' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 500, color: '#22c55e', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Check size={14} /> Mensagem agendada ativa
              </p>
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0 }}>
                As mensagens serão enviadas automaticamente conforme o agendamento.
              </p>
            </div>
            <button onClick={onDeactivate} style={{
              ...styles.btn, padding: '8px 16px', fontSize: '13px',
              backgroundColor: 'transparent', color: '#f59e0b',
              border: '1px solid #f59e0b40', display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              <AlertCircle size={14} /> Pausar
            </button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0 0 12px' }}>
              Ao ativar, as mensagens serão enviadas automaticamente conforme o agendamento configurado.
            </p>
            <button onClick={onActivate} style={{
              ...styles.btn, ...styles.btnPrimary, padding: '10px 20px', fontSize: '14px',
              backgroundColor: ACCENT, display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <Play size={16} /> {f.status === 'paused' ? 'Retomar' : 'Ativar'}
            </button>
          </>
        )}
      </CollapsibleSection>

      {/* 4. Manual Test (collapsed by default) */}
      <CollapsibleSection title="Teste manual" icon={Send} defaultOpen={false} colors={colors} accentColor={colors.textSecondary}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            {isBrPhoneInput(testNumber) && (
              <span style={{
                position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                fontSize: '14px', color: colors.textTertiary, fontWeight: 500, pointerEvents: 'none',
              }}>
                +55
              </span>
            )}
            <input
              value={testNumber}
              onChange={e => {
                // Máscara BR para DDD + número; com "+" ou DDI vale como veio.
                const masked = maskFollowupPhoneInput(e.target.value);
                if (masked !== null) setTestNumber(masked);
              }}
              placeholder="(11) 99999-9999"
              title="Número do Brasil com DDD, ou de outro país começando com +"
              style={{ ...styles.input, flex: 1, paddingLeft: isBrPhoneInput(testNumber) ? '46px' : '12px' }}
              maxLength={20}
            />
          </div>
          {onOpenContactPicker && (
            <button
              onClick={onOpenContactPicker}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                ...styles.btn, ...styles.btnOutline, padding: '8px 12px', fontSize: '12px',
              }}
              title="Buscar contato"
            >
              <UserPlus size={14} />
            </button>
          )}
          <button
            onClick={onSendNow}
            disabled={sendingTest || !testNumber.trim() || messages.length === 0}
            style={{
              ...styles.btn, ...styles.btnPrimary, padding: '10px 16px', fontSize: '13px',
              opacity: sendingTest || !testNumber.trim() || messages.length === 0 ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            {sendingTest ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
            {sendingTest ? 'Enviando...' : 'Enviar agora'}
          </button>
        </div>
      </CollapsibleSection>

      {/* 5. Advanced Config (WhatsApp Business only) */}
      {isBusinessChannel && (
        <CollapsibleSection title="Configurações avançadas" icon={Settings} defaultOpen={false} colors={colors} accentColor={colors.textSecondary}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                Template de Fallback (janela 24h fechada)
              </label>
              {templateUi ? (
                <TemplatePicker
                  templates={approvedTemplates}
                  value={f.business_config?.fallback_template_name || ''}
                  language={f.business_config?.fallback_template_language || ''}
                  onChange={(tpl) => onUpdateFollowup({
                    business_config: {
                      ...f.business_config,
                      fallback_template_name: tpl?.name || '',
                      fallback_template_language: tpl?.language || 'pt_BR',
                    },
                  })}
                  capabilities={templateCapabilities('fallback', flags)}
                  colors={colors}
                  noneLabel="Nenhum (não enviar fallback)"
                />
              ) : (
              <select
                value={f.business_config?.fallback_template_name || ''}
                onChange={e => onUpdateFollowup({
                  business_config: { ...f.business_config, fallback_template_name: e.target.value }
                })}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                  color: colors.textPrimary, fontSize: '14px', outline: 'none',
                }}
              >
                <option value="">Nenhum (não enviar fallback)</option>
                {approvedTemplates.map(t => (
                  <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                ))}
              </select>
              )}
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Intervalo de retentativa (horas)
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={f.business_config?.retry_interval_hours || 6}
                  onChange={e => onUpdateFollowup({
                    business_config: { ...f.business_config, retry_interval_hours: parseInt(e.target.value) || 6 }
                  })}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Maximo de tentativas
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={f.business_config?.max_retries || 3}
                  onChange={e => onUpdateFollowup({
                    business_config: { ...f.business_config, max_retries: parseInt(e.target.value) || 3 }
                  })}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </>
  );
};

export default ScheduledMessageDetailsTab;
